import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { CategoriaProveedorPublico } from '../types';

interface ArbolCategoriasProveedorProps {
  categorias: CategoriaProveedorPublico[];
  subcategoriasSeleccionadas: number[];
  onToggleSubcategoria: (id: number) => void;
  onToggleCategoriaCompleta: (ids: number[]) => void;
  onLimpiar: () => void;
}

function CategoriaItem({
  categoria,
  subcategoriasSeleccionadas,
  onToggleSubcategoria,
  onToggleCategoriaCompleta,
}: {
  categoria: CategoriaProveedorPublico;
  subcategoriasSeleccionadas: number[];
  onToggleSubcategoria: (id: number) => void;
  onToggleCategoriaCompleta: (ids: number[]) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const esHoja = categoria.subcategorias.length === 0;
  const subcatIds = categoria.subcategorias.map((s) => s.id);
  const seleccionadasEnCategoria = subcatIds.filter((id) => subcategoriasSeleccionadas.includes(id));
  const todasSeleccionadas = esHoja
    ? subcategoriasSeleccionadas.includes(categoria.id)
    : seleccionadasEnCategoria.length === subcatIds.length && subcatIds.length > 0;
  const algunaSeleccionada = !esHoja && seleccionadasEnCategoria.length > 0 && !todasSeleccionadas;
  const countActivas = esHoja
    ? (subcategoriasSeleccionadas.includes(categoria.id) ? 1 : 0)
    : seleccionadasEnCategoria.length;

  const handleCheckbox = () => {
    if (esHoja) {
      onToggleSubcategoria(categoria.id);
    } else {
      onToggleCategoriaCompleta(subcatIds);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 0.25,
          '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
        }}
        data-testid={`categoria-row-${categoria.nombre}`}
      >
        <Checkbox
          size="small"
          checked={todasSeleccionadas}
          indeterminate={algunaSeleccionada}
          onChange={handleCheckbox}
          inputProps={{
            'aria-label': esHoja
              ? `Seleccionar ${categoria.nombre}`
              : `Seleccionar todas las subcategorías de ${categoria.nombre}`,
          }}
          data-testid={`checkbox-categoria-${categoria.nombre}`}
          sx={{ py: 0.25, px: 0.5 }}
        />
        <Typography
          variant="body2"
          sx={{ flex: 1, fontWeight: 600, fontSize: '0.8rem', cursor: esHoja ? 'default' : 'pointer' }}
          onClick={() => !esHoja && setExpanded((v) => !v)}
          data-testid={`label-categoria-${categoria.nombre}`}
        >
          {categoria.nombre}
        </Typography>
        {countActivas > 0 && !expanded && (
          <Chip
            label={countActivas}
            size="small"
            color="secondary"
            sx={{ height: 18, fontSize: '0.7rem', mr: 0.5 }}
            data-testid={`badge-categoria-${categoria.nombre}`}
          />
        )}
        {!esHoja && (
          <IconButton
            size="small"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? `Colapsar ${categoria.nombre}` : `Expandir ${categoria.nombre}`}
            data-testid={`toggle-categoria-${categoria.nombre}`}
            sx={{ p: 0.25 }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>

      {!esHoja && (
        <Collapse in={expanded} unmountOnExit>
          <List dense disablePadding sx={{ pl: 3 }}>
            {categoria.subcategorias.map((subcat) => (
              <ListItem
                key={subcat.id}
                disablePadding
                sx={{ py: 0 }}
                data-testid={`subcat-item-${subcat.nombre}`}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <Checkbox
                    size="small"
                    checked={subcategoriasSeleccionadas.includes(subcat.id)}
                    onChange={() => onToggleSubcategoria(subcat.id)}
                    inputProps={{ 'aria-label': subcat.nombre }}
                    data-testid={`checkbox-subcat-${subcat.nombre}`}
                    sx={{ py: 0.25, px: 0 }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={subcat.nombre}
                  primaryTypographyProps={{ variant: 'body2', fontSize: '0.78rem' }}
                />
              </ListItem>
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );
}

export function ArbolCategoriasProveedor({
  categorias,
  subcategoriasSeleccionadas,
  onToggleSubcategoria,
  onToggleCategoriaCompleta,
  onLimpiar,
}: ArbolCategoriasProveedorProps) {
  const totalActivas = subcategoriasSeleccionadas.length;

  return (
    <Box data-testid="arbol-categorias-proveedor">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
          Categorías del Proveedor
          {totalActivas > 0 && (
            <Typography
              component="span"
              variant="caption"
              sx={{ ml: 0.5, color: 'text.secondary' }}
              data-testid="header-activas-count"
            >
              · {totalActivas} activa{totalActivas !== 1 ? 's' : ''}
            </Typography>
          )}
        </Typography>
        {totalActivas > 0 && (
          <Link
            component="button"
            variant="caption"
            onClick={onLimpiar}
            underline="hover"
            data-testid="limpiar-subcategorias"
            sx={{ color: 'secondary.main', cursor: 'pointer' }}
          >
            Limpiar
          </Link>
        )}
      </Box>

      <Box data-testid="lista-categorias-proveedor">
        {categorias.map((cat) => (
          <CategoriaItem
            key={cat.id}
            categoria={cat}
            subcategoriasSeleccionadas={subcategoriasSeleccionadas}
            onToggleSubcategoria={onToggleSubcategoria}
            onToggleCategoriaCompleta={onToggleCategoriaCompleta}
          />
        ))}
      </Box>
    </Box>
  );
}
